import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ componentStack: errorInfo.componentStack || null });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full">
            <h2 className="text-xl font-bold text-red-600 mb-4">Algo salio mal</h2>
            <p className="text-gray-600 mb-4">
              Ocurrio un error inesperado. Intenta recargar la pagina.
            </p>
            <pre className="bg-gray-100 rounded-lg p-4 text-sm text-gray-800 overflow-auto max-h-40 mb-4">
              {this.state.error?.message}
            </pre>
            {this.state.componentStack && (
              <details className="mb-4">
                <summary className="text-sm text-gray-500 cursor-pointer">Stack del componente</summary>
                <pre className="bg-gray-100 rounded-lg p-4 text-xs text-gray-600 overflow-auto max-h-60 mt-2">
                  {this.state.componentStack}
                </pre>
              </details>
            )}
            {(() => {
              const w = window as unknown as Record<string, unknown>;
              const protocolData = w.__PROTOCOL_DATA;
              if (!protocolData) return null;
              return (
                <details className="mb-4" open>
                  <summary className="text-sm text-orange-600 font-semibold cursor-pointer">Datos del protocolo (debug)</summary>
                  <pre className="bg-orange-50 rounded-lg p-4 text-xs text-gray-700 overflow-auto max-h-80 mt-2">
                    {JSON.stringify(protocolData, null, 2)}
                  </pre>
                </details>
              );
            })()}
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Recargar pagina
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
